import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer, Event } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs from "dayjs";
import { useStyles } from "../../../theme/appStyles";
import { useGetAllInvoiceQuery, useGetDriverQuery } from "../../../Api/attoDeskApi";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Box,
  MenuItem,
  Typography,
  FormControl,
  Select,
  InputLabel,
  SelectChangeEvent
} from "@mui/material";

// Define localizer
const localizer = momentLocalizer(moment);

// Define TypeScript interface for a booking
interface CalendarEvent extends Event {
  title: string;
  start: Date;
  end: Date;
  workOrderId: string;
  driverIds?: string[]; // List of driver IDs associated with the booking
}

const BookingCalendar = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]); // To store the list of drivers
  const [selectedDriver, setSelectedDriver] = useState<string>("");
  const classes = useStyles();
  const navigate = useNavigate();

  // Fetch work orders (invoices data)
  const { data: invoiceData } = useGetAllInvoiceQuery({
    searchText: "",
    id: "",
    quoteWorkOrderCustomerId: "",
    quoteWorkOrderCustomerProjectId: "",
  });

  // Fetch drivers data
  const { data: driverData } = useGetDriverQuery({ searchText: "", id: "" });
 // const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
 const capitalize = (str: string | null | undefined): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
  // Format and fetch bookings
  useEffect(() => {
    let bookings: CalendarEvent[] = [];
  
    if (invoiceData && Array.isArray(invoiceData.data)) {
    
      // bookings = invoiceData.data.flatMap((invoice: any) =>
      //   invoice.quoteWorkOrderSale
      //     .filter((sale: any) => Array.isArray(invoice?.drivers) && invoice.drivers.length > 0) // Ensure driver is assigned
      //     .map((sale: any) => ({
      //       title: `${invoice.quoteWorkOrderNo} | ${sale.productName} | ${dayjs(sale.departureDateTime).format('h:mm A')} ${
      //         sale.returnDateTime ? `- ${dayjs(sale.returnDateTime).format('h:mm A')}` : ''
      //       } | ${sale.transferTypeFrom} to ${sale.transferTypeTo} ${
      //         invoice.drivers.length
      //           ? `(Drivers: ${invoice.drivers
      //               .map((driver: { driverFirstName: string; driverLastName: string }) =>
      //                 `${capitalize(driver.driverFirstName)} ${capitalize(driver.driverLastName)}`
      //               )
      //               .join(', ')})`
      //           : ''
      //       }`,
      //       start: new Date(sale.departureDateTime),
      //       end: sale.returnDateTime ? new Date(sale.returnDateTime) : new Date(sale.departureDateTime),
      //       allDay: false,
      //       workOrderId: invoice.work_order_id,
  
      //       // Ensure driver IDs are stored for filtering
      //       driverIds: invoice.drivers?.map((driver: any) => driver.id) || [],
      //     }))



   bookings = invoiceData.data.flatMap((invoice: any) =>
    invoice.quoteWorkOrderSale
      .filter((sale: any) => Array.isArray(invoice?.drivers) && invoice.drivers.length > 0) // Ensure driver is assigned
      .map((sale: any) => {
        // Collect all datetime values from pickups, dropoffs, returnPickups, returnDropoffs
        const allTimes = [
          ...(sale.pickups || []).map((p: any) => p.dateTime).filter(Boolean),
          ...(sale.dropoffs || []).map((d: any) => d.dateTime).filter(Boolean),
          ...(sale.returnPickups || []).map((rp: any) => rp.dateTime).filter(Boolean),
          ...(sale.returnDropoffs || []).map((rd: any) => rd.dateTime).filter(Boolean),
        ].filter(dt => dayjs(dt).isValid());

        // Determine start and end times
        const startTime = allTimes.length 
          ? new Date(Math.min(...allTimes.map(t => new Date(t).getTime())))
          : new Date();
        const endTime = allTimes.length 
          ? new Date(Math.max(...allTimes.map(t => new Date(t).getTime())))
          : startTime;

        // Build location string
        const firstPickup = (sale.pickups || [])[0];
        const lastDropoff = [...(sale.dropoffs || []), ...(sale.returnDropoffs || [])].slice(-1)[0];
        
        const fromLocation = firstPickup?.city || firstPickup?.address || sale.transferTypeFrom || '';
        const toLocation = lastDropoff?.city || lastDropoff?.address || sale.transferTypeTo || '';

        // Build time display
        const timeDisplay = `${dayjs(startTime).format('h:mm A')}${
          endTime.getTime() !== startTime.getTime() ? ` - ${dayjs(endTime).format('h:mm A')}` : ''
        }`;

        return {
          title: `${invoice.quoteWorkOrderNo} | ${sale.productName} | ${timeDisplay} | ${fromLocation} to ${toLocation}${
            invoice.drivers.length
              ? ` (Drivers: ${invoice.drivers
                  .map((driver: { driverFirstName: string; driverLastName: string }) =>
                    `${capitalize(driver.driverFirstName)} ${capitalize(driver.driverLastName)}`
                  )
                  .join(', ')})`
              : ''
          }`,
          start: startTime,
          end: endTime,
          allDay: false,
          workOrderId: invoice.work_order_id,

          // Ensure driver IDs are stored for filtering
          driverIds: invoice.drivers?.map((driver: any) => driver.id) || [],
        };
      })




      );
    }
  
    setEvents(bookings);
  }, [invoiceData]);
  
  

  // Update drivers list when driverData is fetched and filter based on role
  useEffect(() => {
    if (driverData && Array.isArray(driverData.data)) {
      const filteredDrivers = driverData.data.filter((driver: any) => driver.driverUserRole === 'driver');
      setDrivers(filteredDrivers);
    }
  }, [driverData]);

  // Handle driver selection change
  const handleDriverChange = (event: any) => {
    const selectedDriver = event.target.value;
    setSelectedDriver(selectedDriver); // Update state with the selected driver
  
  };

  const filteredEvents = events.filter(event => event.driverIds && event.driverIds.length > 0);

const driverFilteredEvents = selectedDriver
  ? filteredEvents.filter(event => event.driverIds?.includes(selectedDriver))
  : filteredEvents;



  const handleEventClick = (event: CalendarEvent) => {
    const workOrderId = event.workOrderId;
    navigate(`/work-order/history/${workOrderId}`);
  };

  return (


    <>
       <Box mb={2}>
    <Grid item lg={6} md={6} sm={12} xs={12} padding={2}>
      <Grid container alignItems="center">
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Typography variant="subtitle1">
            Select Driver
          </Typography>
        </Grid>
        <Grid
          item
          lg={3}
          md={3}
          sm={12}
          xs={12}
          display="flex"
          alignItems="center">

<Select
  className={classes.textField}
  value={selectedDriver}
  onChange={handleDriverChange}
  displayEmpty
  sx={{
    height: "32px",
    fontSize: "14px",
    padding: "4px",
    minWidth: "120px"
  }}
>
  <MenuItem value="">All Drivers</MenuItem>
  {drivers.map((driver: any) => (
    <MenuItem key={driver.id} value={driver.id}>
      {`${capitalize(driver.driverFirstName)} ${capitalize(driver.driverLastName)}`}
    </MenuItem>
  ))}
</Select>


        </Grid>
      </Grid>
    </Grid>
    </Box>
    
    
    <div style={{ height: 600, padding: "20px", backgroundColor: "#f8f9fa", borderRadius: "10px", boxShadow: "0px 4px 15px rgba(0,0,0,0.1)" , marginTop:"15px"}}>
     
        {/* Calendar */}
        <Calendar
          localizer={localizer}
          events={driverFilteredEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%", whiteSpace: "none" }}
          eventPropGetter={(event: any) => ({
            style: {
              backgroundColor: "green",
              color: "#fff",
              borderRadius: "4px",
              padding: "5px",
              fontSize: "11px",
              whiteSpace: 'normal !important',
              wordBreak: "break-word",
              // overflow: "visible",
            },
          })}
          onSelectEvent={handleEventClick} 
          />
      </div></>
  );
};

export default BookingCalendar;
